import { Link } from "react-router";
import { Alerta, Badge, BarraProgreso, Button, Card, DataList, Dialog, Icono, ListaOrdenable, MetricCard, Row, SelectorArchivos, Spinner, Stack, TextField } from "@/components";
import type { ItemComputo, ItemFicha, ItemPresupuesto } from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatMoneda } from "@/lib/format";
import { consumidoTotal, ETIQUETA_ESTADO, formatCantidad, numeroItem, porcentaje, primerNumero, textoConsumido, textoPartes, tonoEstado } from "@/lib/presupuesto";
import { DialogosItem, EtiquetasRubro } from "./EdicionRubro";
import type { EdicionRubro } from "./useEdicionRubro";
import { usePresupuestoTab, type Desglose } from "./usePresupuestoTab";

// Cada fila de la tabla es un ítem por rubro (con su lugar: el número del rubro − 1, que `numeroItem` vuelve a sumar;
// con Trámites previos primero, el primero es −1 → "0"), salvo la última: la fila de totales.
type Fila = { tipo: "item"; i: ItemPresupuesto; pos: number } | { tipo: "total" };

function filasDe(rubros: ItemPresupuesto[]): Fila[] {
  const corrimiento = primerNumero(rubros.map((r) => r.rubro)) - 1;
  return [...rubros.map((i, pos): Fila => ({ tipo: "item", i, pos: pos + corrimiento })), { tipo: "total" }];
}

function claveFila(f: Fila): string | number {
  return f.tipo === "total" ? "totales" : f.i.rubro.id;
}

function textoItems(i: ItemPresupuesto): string {
  const items = i.cantidad_items === 0 ? "Sin ítems" : i.cantidad_items === 1 ? "1 ítem" : `${i.cantidad_items} ítems`;
  return i.etiquetas.length ? `${items} · ${i.etiquetas.join(", ")}` : items;
}

const claseLinkChico = "text-chico font-medium text-primario underline-offset-2 hover:underline";
const usd = (v: string | null) => (v === null ? "—" : formatMoneda(v, "USD"));
const enSuMoneda = (v: string | null, i: ItemComputo) => (v === null ? "—" : formatMoneda(v, i.moneda));

const columnasItems = (posRubro: number, items: ItemFicha[]) => [
  { clave: "numero", titulo: "N.º", render: (i: ItemFicha) => <span className="tabular-nums text-texto-suave">{numeroItem(posRubro, items.indexOf(i))}</span> },
  { clave: "descripcion", titulo: "Ítem", render: (i: ItemFicha) => <span className="whitespace-pre-line">{i.descripcion}</span> },
  { clave: "cantidad", titulo: "Cantidad", render: (i: ItemFicha) => formatCantidad(i) },
  { clave: "materiales", titulo: "Materiales (unit.)", render: (i: ItemFicha) => enSuMoneda(i.precio_materiales, i) },
  { clave: "mano_obra", titulo: "Mano de obra (unit.)", render: (i: ItemFicha) => enSuMoneda(i.precio_mano_obra, i) },
  {
    clave: "subtotal",
    titulo: "Subtotal",
    render: (i: ItemFicha) => (
      <Stack espacio={1}>
        <span>{usd(i.subtotal)}</span>
        {i.moneda === "ARS" && i.subtotal_moneda !== null && <span className="text-chico text-texto-suave">{formatMoneda(i.subtotal_moneda, "ARS")}</span>}
      </Stack>
    ),
  },
  // La relación de cada ítem, sólo si algún ítem la tiene: lo aceptado (líneas de presupuestos aceptados vinculadas
  // al ítem) con links a esos presupuestos, y lo facturado (facturas con el ítem) y pagado con links a las facturas.
  ...(items.some((i) => i.aceptado_usd !== null)
    ? [
        {
          clave: "aceptado",
          titulo: "Aceptado",
          render: (i: ItemFicha) => (
            <Stack espacio={1}>
              <span>{usd(i.aceptado_usd)}</span>
              {i.aceptados.map((a) => (
                <Link key={a.cotizacion_id} to={`/cotizaciones/${a.cotizacion_id}`} className={claseLinkChico}>
                  {i.aceptados.length > 1 ? `${a.proveedor} (${formatMoneda(a.usd, "USD")})` : a.proveedor}
                </Link>
              ))}
            </Stack>
          ),
        },
      ]
    : []),
  ...(items.some((i) => i.facturado_usd !== null)
    ? [
        {
          clave: "facturado",
          titulo: "Facturado",
          render: (i: ItemFicha) => (
            <Stack espacio={1}>
              <span>{usd(i.facturado_usd)}</span>
              {i.facturas.map((f) => (
                <Link key={f.id} to={`/gastos/${f.id}`} className={claseLinkChico}>
                  {f.comprobante_nro ?? f.descripcion}
                </Link>
              ))}
            </Stack>
          ),
        },
        { clave: "pagado", titulo: "Pagado", render: (i: ItemFicha) => usd(i.pagado_usd) },
      ]
    : []),
];

function textoItemsEnUsd(n: number): string | null {
  if (n === 0) return null;
  const cuales = n === 1 ? "1 ítem tiene los precios" : `${n} ítems tienen los precios`;
  return `${cuales} en dólares (cargados antes del presupuesto en pesos): al guardar el tipo de cambio se pasan a pesos, sin que cambie su valor en dólares.`;
}

interface PropsDesglose {
  rubro: ItemPresupuesto["rubro"];
  pos: number;
  desglose: Desglose;
  esAdmin: boolean;
  edicion: EdicionRubro;
  ordenando: boolean;
  alternarOrden: () => void;
  mover: (ids: number[]) => void;
}

/** Los ítems del rubro, desplegados debajo de su fila: el admin los agrega, edita, saca y reordena, y cambia las etiquetas. */
function DesgloseRubro({ rubro, pos, desglose, esAdmin, edicion, ordenando, alternarOrden, mover }: PropsDesglose) {
  if (desglose.cargando) {
    return (
      <div className="grid place-items-center py-4 text-primario">
        <Spinner />
      </div>
    );
  }
  if ("error" in desglose) return <Alerta texto={desglose.error} />;
  const { ficha } = desglose;
  const partes = textoPartes(ficha.materiales, ficha.mano_obra);
  const acciones = esAdmin
    ? (i: ItemComputo) => (
        <Row espacio={2}>
          <Button variante="secundario" tamanio="sm" onClick={() => edicion.abrirEditar(rubro.id, i)}>
            Editar
          </Button>
          <Button variante="fantasma" tamanio="sm" onClick={() => edicion.abrirBorrar(rubro.id, i)}>
            Sacar
          </Button>
        </Row>
      )
    : undefined;
  return (
    <Stack espacio={3}>
      {esAdmin && (
        <Row justificar="fin" envolver espacio={2}>
          {ficha.items.length > 1 && (
            <Button variante={ordenando ? "primario" : "secundario"} tamanio="sm" onClick={alternarOrden}>
              {ordenando ? "Listo" : "Ordenar ítems"}
            </Button>
          )}
          {!ordenando && (
            <Button tamanio="sm" onClick={() => edicion.abrirNuevo(rubro.id)}>
              Agregar ítem
            </Button>
          )}
        </Row>
      )}
      {ordenando ? (
        <ListaOrdenable
          etiqueta={`Orden de los ítems de ${rubro.nombre}`}
          elementos={ficha.items.map((i, n) => ({
            id: i.id,
            texto: `${numeroItem(pos, n)} ${i.descripcion}`,
            contenido: (
              <span className="whitespace-pre-line">
                <span className="tabular-nums text-texto-suave">{numeroItem(pos, n)}</span> {i.descripcion}
              </span>
            ),
          }))}
          onMover={mover}
        />
      ) : (
        <DataList
          etiqueta={`Ítems de ${rubro.nombre}`}
          columnas={columnasItems(pos, ficha.items)}
          filas={ficha.items}
          claveFila={(i) => i.id}
          acciones={acciones}
          vacio="Este rubro todavía no tiene ítems."
        />
      )}
      <Row justificar="entre" envolver espacio={2}>
        <span className="text-chico text-texto-suave">{partes ?? ""}</span>
        <Link to={`/presupuesto/rubros/${rubro.id}`} className={claseLinkChico}>
          Comparar presupuestos recibidos
        </Link>
      </Row>
      {esAdmin && <EtiquetasRubro e={edicion} rubroId={rubro.id} nombre={rubro.nombre} etiquetas={ficha.resumen.etiquetas} />}
    </Stack>
  );
}

export function PresupuestoTab() {
  const t = usePresupuestoTab();
  const p = t.presupuesto;

  const columnas = [
    {
      clave: "rubro",
      titulo: "Rubro",
      render: (f: Fila) =>
        f.tipo === "total" ? (
          <strong>Total</strong>
        ) : (
          <Stack espacio={1}>
            <Row espacio={2}>
              <button
                type="button"
                aria-expanded={t.desglose(f.i.rubro.id) !== null}
                onClick={() => t.alternar(f.i.rubro.id)}
                className="inline-flex items-center gap-1 text-left font-medium text-primario underline-offset-2 hover:underline"
              >
                <Icono nombre="flecha" className={cn("size-4 transition-transform", t.desglose(f.i.rubro.id) !== null && "rotate-90")} />
                <span className="tabular-nums">{f.pos + 1}.</span> {f.i.rubro.nombre}
              </button>
              {!f.i.rubro.activo && <Badge>Archivado</Badge>}
            </Row>
            <span className="text-chico text-texto-suave">{textoItems(f.i)}</span>
          </Stack>
        ),
    },
    {
      clave: "presupuesto",
      titulo: "Presupuesto",
      render: (f: Fila) => (f.tipo === "total" ? formatMoneda(p!.totales.presupuesto, "USD") : f.i.presupuesto === null ? "—" : formatMoneda(f.i.presupuesto, "USD")),
    },
    {
      clave: "aceptado",
      titulo: "Aceptado",
      render: (f: Fila) => (f.tipo === "total" || f.i.aceptado_usd === null ? "—" : formatMoneda(f.i.aceptado_usd, "USD")),
    },
    {
      clave: "facturado",
      titulo: "Facturado",
      render: (f: Fila) => formatMoneda(f.tipo === "total" ? p!.totales.facturado : f.i.facturado, "USD"),
    },
    {
      clave: "pagado",
      titulo: "Pagado",
      render: (f: Fila) => formatMoneda(f.tipo === "total" ? p!.totales.pagado : f.i.pagado, "USD"),
    },
    {
      clave: "disponible",
      titulo: "Disponible",
      render: (f: Fila) => {
        const disponible = f.tipo === "total" ? p!.totales.disponible : f.i.disponible;
        if (disponible === null) return "—";
        return <span className={Number(disponible) < 0 ? "text-peligro" : undefined}>{formatMoneda(disponible, "USD")}</span>;
      },
    },
    {
      clave: "consumido",
      titulo: "Consumido",
      render: (f: Fila) =>
        f.tipo === "total" ? (
          consumidoTotal(p!)
        ) : (
          <Stack espacio={1} className="min-w-32">
            <BarraProgreso porcentaje={porcentaje(f.i)} tono={tonoEstado(f.i.estado)} etiqueta={`Consumido de ${f.i.rubro.nombre}`} />
            <span className="text-chico text-texto-suave">{textoConsumido(f.i)}</span>
          </Stack>
        ),
    },
    {
      clave: "estado",
      titulo: "Estado",
      render: (f: Fila) => (f.tipo === "total" ? null : <Badge tono={tonoEstado(f.i.estado)}>{ETIQUETA_ESTADO[f.i.estado]}</Badge>),
    },
  ];

  const facturadoSinPresupuesto = p ? Number(p.totales.facturado_sin_presupuesto) : 0;

  return (
    <Stack espacio={6}>
      <Row justificar="entre" envolver espacio={2}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Presupuesto</h1>
        {t.esAdmin && (
          <Row envolver espacio={2}>
            {p && p.items.length > 1 && (
              <Button variante={t.ordenando === "rubros" ? "primario" : "secundario"} onClick={() => t.alternarOrden("rubros")}>
                {t.ordenando === "rubros" ? "Listo" : "Ordenar rubros"}
              </Button>
            )}
            <SelectorArchivos
            etiqueta="Planilla de cómputo y presupuesto (Excel)"
            textoBoton={t.leyendoPlanilla ? "Leyendo…" : "Importar planilla"}
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            deshabilitado={t.leyendoPlanilla}
            onElegir={(a) => void t.elegirPlanilla(a)}
            />
          </Row>
        )}
      </Row>
      <p className="text-texto-suave">Cada rubro suma sus ítems (cantidad × precio unitario en pesos, pasado a dólares con el tipo de cambio del presupuesto). Tocá un rubro para ver sus ítems.</p>
      <Alerta texto={t.errorPlanilla} />
      {t.resultado && (
        <p role="status" className="rounded-control bg-exito-suave px-3 py-2 text-chico text-exito">
          {["Planilla importada.", ...t.resultado].join(" ")}
        </p>
      )}

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.error && <Alerta texto={t.error} />}

      {p && (
        <>
          <Card>
            <Stack espacio={3}>
              <h2 className="text-titulo font-light tracking-tight">Tipo de cambio del presupuesto</h2>
              {t.esAdmin ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void t.guardarTipoCambio();
                  }}
                >
                  <Row alinear="fin" envolver espacio={2}>
                    <TextField
                      etiqueta="ARS por USD"
                      inputMode="decimal"
                      value={t.tipoCambio}
                      onChange={(e) => t.cambiarTipoCambio(e.target.value)}
                      error={t.errorTipoCambio ?? undefined}
                      ayuda="Si lo cambiás, el valor en dólares de todo el presupuesto se recalcula."
                    />
                    <Button type="submit" cargando={t.guardandoTipoCambio} disabled={!t.tipoCambioCambiado}>
                      Guardar
                    </Button>
                  </Row>
                </form>
              ) : (
                <p>{p.tipo_cambio === null ? "Sin cargar." : `USD 1 = ${formatMoneda(p.tipo_cambio, "ARS")}`}</p>
              )}
              <Alerta texto={p.tipo_cambio === null ? "Falta el tipo de cambio: los precios de los ítems se cargan en pesos y sin él no se pueden pasar a dólares." : null} tono="alerta" />
              {textoItemsEnUsd(p.items_en_usd) && <p className="text-chico text-texto-suave">{textoItemsEnUsd(p.items_en_usd)}</p>}
            </Stack>
          </Card>

          <div className="grid gap-3 sm:grid-cols-3">
            <MetricCard titulo="Presupuesto" valor={formatMoneda(p.totales.presupuesto, "USD")} />
            <MetricCard titulo="Facturado" valor={formatMoneda(p.totales.facturado, "USD")} detalle={`Pagado ${formatMoneda(p.totales.pagado, "USD")}`} />
            <MetricCard titulo="Disponible" valor={formatMoneda(p.totales.disponible, "USD")} />
          </div>

          <Alerta
            texto={facturadoSinPresupuesto > 0 ? `Hay ${formatMoneda(p.totales.facturado_sin_presupuesto, "USD")} facturados en rubros sin presupuesto.` : null}
            tono="alerta"
          />

          <Alerta texto={t.errorOrden} />
          {t.ordenando === "rubros" && (
            <Card>
              <Stack espacio={3}>
                <h2 className="text-titulo font-light tracking-tight">Orden de los rubros</h2>
                <p className="text-chico text-texto-suave">Arrastrá cada rubro desde su agarre o usá las flechas. El número de cada rubro y de sus ítems sale de este orden; se guarda solo.</p>
                <ListaOrdenable
                  etiqueta="Orden de los rubros"
                  elementos={t.rubros.map((r, n) => ({ id: r.rubro.id, texto: `${n + primerNumero(t.rubros.map((x) => x.rubro))}. ${r.rubro.nombre}` }))}
                  onMover={t.moverRubros}
                />
              </Stack>
            </Card>
          )}

          <DataList
            etiqueta="Presupuesto por rubro"
            columnas={columnas}
            filas={filasDe(t.rubros)}
            claveFila={claveFila}
            vacio="No hay rubros para mostrar."
            detalle={(f) => {
              const d = f.tipo === "item" ? t.desglose(f.i.rubro.id) : null;
              if (f.tipo !== "item" || !d) return null;
              const id = f.i.rubro.id;
              return (
                <DesgloseRubro
                  rubro={f.i.rubro}
                  pos={f.pos}
                  desglose={d}
                  esAdmin={t.esAdmin}
                  edicion={t.edicion}
                  ordenando={t.ordenando === id}
                  alternarOrden={() => t.alternarOrden(id)}
                  mover={(ids) => t.moverItems(id, ids)}
                />
              );
            }}
          />
        </>
      )}

      <DialogosItem e={t.edicion} />

      <Dialog abierto={t.importacion !== null} onCambio={(abierto) => !abierto && t.cerrarImportacion()} titulo="Importar planilla">
        {t.importacion && (
          <Stack espacio={4}>
            <p className="text-texto-suave">{t.importacion.archivo.name}</p>
            <ul className="list-disc space-y-1 pl-5">
              {t.lineasVista.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
            {t.hayParaImportar && <p className="text-chico text-texto-suave">Se cargan en pesos los precios unitarios de materiales y mano de obra que tenga la planilla; los que falten se cargan después en cada rubro.</p>}
            <Alerta texto={t.errorImportar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrarImportacion}>
                {t.hayParaImportar ? "Cancelar" : "Listo"}
              </Button>
              {t.hayParaImportar && (
                <Button cargando={t.importando} onClick={() => void t.confirmarImportacion()}>
                  Importar
                </Button>
              )}
            </Row>
          </Stack>
        )}
      </Dialog>
    </Stack>
  );
}
